import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../store/StoreContext';
import { inputCls, Button } from '../ui';

// Unified client-file Notes area: ONE card, two sections —
//   • Office Notes (internal_note_sets, sql/063) — office-only, never reaches crew
//   • Crew Notes   (crew_note_sets, sql/060)     — selectable on estimates, reaches crew
// Both are per-client, timestamped, editable/removable entries. Self-saving and
// always interactive — you do NOT need the modal's Edit mode to add a note.
//
// Composing a note opens a FULL-WIDTH editor that covers the whole notes area
// (more room to write); saving returns to the two-column entry display. Crew-note
// BEHAVIOR is unchanged (same store CRUD + rows the estimate selector uses).

const ACCENTS = {
    office: {
        title: 'Office Notes', badge: 'OFFICE ONLY', badgeCls: 'bg-amber-100 text-amber-600',
        ring: 'border-amber-300 ring-4 ring-amber-100', heading: 'text-amber-700',
        emptyHint: 'No office notes yet — internal only, never shown to the crew.',
        placeholder: 'Internal office note — packing scope, special handling, billing reminders…',
    },
    crew: {
        title: 'Crew Notes', badge: 'REACHES CREW', badgeCls: 'bg-indigo-100 text-indigo-600',
        ring: 'border-indigo-300 ring-4 ring-indigo-100', heading: 'text-indigo-700',
        emptyHint: 'No crew notes yet — selectable on any estimate; reaches the crew on the job.',
        placeholder: 'Notes for the crew — gate code, where to park the truck, pets…',
    },
};

function fmtStamp(ts) {
    if (!ts) return '';
    try {
        return new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
}

// List + header for one section. The editor is rendered at the panel level
// (full width), so this is display-only.
function NoteSection({ section, items, onNew, onEdit, onDelete }) {
    const a = ACCENTS[section];
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">{a.title}</span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${a.badgeCls}`}>{a.badge}</span>
                </div>
                <Button variant="secondary" size="sm" onClick={onNew}>
                    + New note
                </Button>
            </div>

            {items.length === 0 && (
                <p className="text-[11px] text-slate-400 italic">{a.emptyHint}</p>
            )}

            {items.length > 0 && (
                <div className="space-y-1.5">
                    {items.map(s => (
                        <div key={s.id} className="bg-white rounded-lg border border-slate-200 p-2">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    {s.label && <div className="text-xs font-bold text-slate-700 truncate">{s.label}</div>}
                                    {/* Trailing whitespace trimmed for display — dictated
                                        notes often end in a pile of blank lines that
                                        whitespace-pre-wrap would faithfully render as a
                                        half-empty card (Jaylen 2026-07-02). */}
                                    <div className="text-[11px] text-slate-600 whitespace-pre-wrap">{(s.body || '').replace(/\s+$/, '') || <span className="italic text-slate-300">(empty)</span>}</div>
                                </div>
                                <div className="flex gap-1.5 flex-shrink-0">
                                    <Button variant="tertiary" size="sm" onClick={() => onEdit(s)}>Edit</Button>
                                    <Button variant="danger" size="sm" onClick={() => onDelete(s.id)}>Delete</Button>
                                </div>
                            </div>
                            {s.created_at && <div className="text-[9px] text-slate-400 mt-1">{fmtStamp(s.created_at)}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function ClientNotesPanel({ clientId, draft, onDraftChange, onActivity, defaultOfficeLabel }) {
    const { getInternalNoteSets, saveInternalNoteSet, deleteInternalNoteSet, getCrewNoteSets, saveCrewNoteSet, deleteCrewNoteSet } = useStore();
    const [officeSets, setOfficeSets] = useState([]);
    const [crewSets, setCrewSets] = useState([]);
    // The in-progress note draft is OWNED BY THE PARENT (lifted state) so it
    // survives this panel unmounting — tab switches inside the modal, or the
    // modal being dismissed mid-compose — instead of silently vanishing. The
    // parent also folds it into the modal's unsaved-changes close guard.
    // draft: null | { section: 'office'|'crew', id?, label, body }
    // Draft can be controlled by the parent (desktop modal, for its close
    // guard) OR managed internally (estimator app, standalone). Same component
    // both places so Office/Crew notes stay in sync — one source of truth.
    const [localDraft, setLocalDraft] = useState(null);
    const editing = onDraftChange ? draft : localDraft;
    const setEditing = onDraftChange || setLocalDraft;
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // ── Draft preservation (2026-07-02, estimator offline work) ────────
    // Every keystroke stashes the in-progress draft in localStorage so a
    // dead zone, app kill, or crash can't eat a dictated note. Cleared on
    // successful save or explicit Cancel; restored on next open.
    const stashKey = `bbm_note_draft_${clientId}`;
    useEffect(() => {
        if (!clientId || editing) return;
        try {
            const raw = localStorage.getItem(stashKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.body?.trim()) setEditing(parsed);
            }
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);
    useEffect(() => {
        if (!clientId) return;
        try {
            if (editing?.body?.trim()) localStorage.setItem(stashKey, JSON.stringify(editing));
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing]);
    const clearStash = () => { try { localStorage.removeItem(stashKey); } catch { /* ignore */ } };

    const reloadOffice = useCallback(async () => {
        if (!clientId) return;
        try { setOfficeSets((await getInternalNoteSets(clientId)) || []); } catch { /* non-fatal */ }
    }, [clientId, getInternalNoteSets]);
    const reloadCrew = useCallback(async () => {
        if (!clientId) return;
        try { setCrewSets((await getCrewNoteSets(clientId)) || []); } catch { /* non-fatal */ }
    }, [clientId, getCrewNoteSets]);

    useEffect(() => { reloadOffice(); reloadCrew(); }, [reloadOffice, reloadCrew]);

    // Estimator app prefills the office-note label ("<Estimator>'s Walkthrough
    // note") so notes it creates stay identifiable; desktop leaves it blank.
    const startNew = (section) => setEditing({ section, label: section === 'office' && defaultOfficeLabel ? defaultOfficeLabel : '', body: '' });
    const startEdit = (section, s) => setEditing({ section, id: s.id, label: s.label || '', body: s.body || '' });

    const save = async () => {
        if (!editing) return;
        if (!editing.body?.trim()) { setEditing(null); return; }
        setSaving(true);
        try {
            // Trailing whitespace stripped at save too, so rows stay clean at
            // the source (display also trims, covering pre-existing rows).
            const cleanBody = (editing.body || '').replace(/\s+$/, '');
            if (editing.section === 'office') {
                await saveInternalNoteSet({ id: editing.id || undefined, client_id: clientId, label: (editing.label || '').trim() || null, body: cleanBody });
                await reloadOffice();
            } else {
                await saveCrewNoteSet({ id: editing.id || undefined, client_id: clientId, label: (editing.label || '').trim() || `Crew notes · ${new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`, body: cleanBody });
                await reloadCrew();
            }
            clearStash();
            setSaveError(null);
            setEditing(null);
            onActivity?.();
        } catch (e) {
            // Save failed (usually no signal) — the draft is already
            // stashed on-device by the keystroke effect; keep the editor
            // open and say so instead of losing anything.
            setSaveError('Couldn’t reach the server — your note is kept on this phone. Tap Save again when you have signal.');
        } finally { setSaving(false); }
    };
    const removeOffice = async (id) => { try { await deleteInternalNoteSet(id); setOfficeSets(prev => prev.filter(s => s.id !== id)); } catch { /* non-fatal */ } };
    const removeCrew = async (id) => { try { await deleteCrewNoteSet(id); setCrewSets(prev => prev.filter(s => s.id !== id)); } catch { /* non-fatal */ } };

    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">📝 Notes</h3>

            {editing ? (
                (() => {
                    const a = ACCENTS[editing.section];
                    return (
                        <div className={`bg-white rounded-lg border-2 p-4 ${a.ring}`}>
                            <div className="flex items-center gap-2 mb-3">
                                <span className={`text-[11px] font-bold uppercase tracking-wider ${a.heading}`}>{editing.id ? 'Edit' : 'New'} {editing.section === 'office' ? 'office note' : 'crew note'}</span>
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${a.badgeCls}`}>{a.badge}</span>
                            </div>
                            <input className={`${inputCls} text-sm mb-2`} placeholder="Label (optional — e.g. Jul 2026 move)" value={editing.label} onChange={e => setEditing(c => ({ ...c, label: e.target.value }))} />
                            <textarea className={`${inputCls} text-sm`} rows={10} autoFocus placeholder={a.placeholder} value={editing.body} onChange={e => setEditing(c => ({ ...c, body: e.target.value }))} />
                            {saveError && (
                                <div className="mt-2 text-[12px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                    {saveError}
                                </div>
                            )}
                            <div className="flex items-center gap-2 mt-3">
                                <Button variant="secondary" onClick={() => { clearStash(); setSaveError(null); setEditing(null); }}>Cancel</Button>
                                <Button
                                    className="flex-1"
                                    onClick={save}
                                    loading={saving}
                                    disabled={!editing.body?.trim()}
                                    leftIcon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                                >
                                    Save note
                                </Button>
                            </div>
                        </div>
                    );
                })()
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <NoteSection section="office" items={officeSets} onNew={() => startNew('office')} onEdit={(s) => startEdit('office', s)} onDelete={removeOffice} />
                    <NoteSection section="crew" items={crewSets} onNew={() => startNew('crew')} onEdit={(s) => startEdit('crew', s)} onDelete={removeCrew} />
                </div>
            )}
        </div>
    );
}
